import React, { useEffect, useState } from 'react';
import { X, Star, Loader2, Phone, Search, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { adminAPI } from '../services/api';

const EMPTY = { product: null, rating: 5, title: '', comment: '', customer_name: '', customer_email: '', order_number: '', channel: 'phone', received_at: new Date().toISOString().slice(0, 10), lang: 'fr', consent: false };

/** Admin: record a genuine review received by phone / e-mail / in store (flagged « collected by the merchant »). */
const ManualReviewModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState(EMPTY);
  const [channels, setChannels] = useState([]);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  useEffect(() => { adminAPI.manualReviewChannels().then(setChannels).catch(() => setChannels([{ id: 'phone', label: 'Téléphone' }, { id: 'email', label: 'E-mail' }])); }, []);
  useEffect(() => {
    if (!q.trim() || form.product) { setResults([]); return undefined; }
    const id = setTimeout(async () => {
      setSearching(true);
      try { setResults((await adminAPI.searchProducts({ q, limit: 8 })).items || []); } catch { setResults([]); } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(id);
  }, [q, form.product]);

  const submit = async () => {
    if (!form.product) { toast.error('Choisissez le produit concerné'); return; }
    setSaving(true);
    try {
      const r = await adminAPI.createManualReview({ ...form, product: undefined, product_id: form.product.id, title: form.title || null, customer_email: form.customer_email || null, order_number: form.order_number || null, received_at: form.received_at || null });
      toast.success(r.message);
      onCreated(r);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Enregistrement impossible'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="manual-review-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-5 border-b bg-[#1e3a5f] text-white flex items-center justify-between">
          <h2 className="text-lg font-bold inline-flex items-center gap-2"><Phone size={20} /> Saisir un avis reçu hors site</h2>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white" data-testid="manual-review-close"><X size={22} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4 text-sm">
          <p className="text-xs text-gray-600 bg-sky-50 border border-sky-200 rounded-lg p-3">Réservé aux avis <strong>authentiques</strong> reçus d'un vrai client (téléphone, e-mail, comptoir). L'avis est publié avec la mention « Avis recueilli par le marchand » ; il n'est marqué « Achat vérifié » que si une commande contenant le produit est retrouvée (n° de commande ou e-mail client).</p>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Produit *</label>
            {form.product ? (
              <div className="flex items-center gap-3 border rounded-lg p-2 bg-gray-50" data-testid="manual-review-product">
                <img src={form.product.image} alt="" className="w-10 h-10 object-contain" />
                <span className="flex-1 truncate font-medium text-gray-900">{form.product.name} <span className="text-gray-400 font-normal">{form.product.brand} · {form.product.ref}</span></span>
                <button type="button" onClick={() => { setForm(f => ({ ...f, product: null })); setQ(''); }} className="text-xs text-blue-600 hover:underline" data-testid="manual-review-product-change">Changer</button>
              </div>
            ) : (
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input data-testid="manual-review-product-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, marque ou référence…" className="pl-9" autoFocus />
                {(results.length > 0 || searching) && (
                  <ul className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-56 overflow-y-auto" data-testid="manual-review-product-results">
                    {searching && <li className="px-3 py-2 text-gray-400 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Recherche…</li>}
                    {results.map(p => (
                      <li key={p.id}><button type="button" onClick={() => setForm(f => ({ ...f, product: p }))} data-testid={`manual-review-product-option-${p.id}`} className="w-full text-left px-3 py-2 hover:bg-orange-50 flex items-center gap-2"><img src={p.image} alt="" className="w-8 h-8 object-contain" /><span className="truncate">{p.name} <span className="text-gray-400">{p.brand} · {p.ref}</span></span></button></li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Note *</label>
              <div className="flex items-center gap-1" data-testid="manual-review-rating">
                {[1, 2, 3, 4, 5].map(i => <button key={i} type="button" onClick={() => setForm(f => ({ ...f, rating: i }))} data-testid={`manual-review-star-${i}`} className="p-0.5"><Star size={24} className={i <= form.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} /></button>)}
                <span className="ml-2 text-gray-600">{form.rating}/5</span>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Canal de réception *</label>
              <select data-testid="manual-review-channel" value={form.channel} onChange={set('channel')} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                {channels.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Nom du client * <span className="text-gray-400">(affiché « Prénom N. »)</span></label>
              <Input data-testid="manual-review-customer-name" value={form.customer_name} onChange={set('customer_name')} placeholder="Jean Dupont" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">E-mail du client <span className="text-gray-400">(non publié, sert à retrouver la commande)</span></label>
              <Input data-testid="manual-review-customer-email" type="email" value={form.customer_email} onChange={set('customer_email')} placeholder="jean@exemple.fr" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">N° de commande <span className="text-gray-400">(optionnel → « Achat vérifié »)</span></label>
              <Input data-testid="manual-review-order-number" value={form.order_number} onChange={set('order_number')} placeholder="CMD-2026-…" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Reçu le</label>
                <Input data-testid="manual-review-received-at" type="date" value={form.received_at} onChange={set('received_at')} max={new Date().toISOString().slice(0, 10)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Langue</label>
                <select data-testid="manual-review-lang" value={form.lang} onChange={set('lang')} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                  {['fr', 'en', 'de', 'es', 'it'].map(l => <option key={l} value={l}>{l.toUpperCase()}</option>)}
                </select>
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Titre <span className="text-gray-400">(optionnel)</span></label>
              <Input data-testid="manual-review-title" value={form.title} onChange={set('title')} placeholder="Montage facile, pièce conforme" maxLength={120} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Avis du client * <span className="text-gray-400">(ses mots, 10 caractères min. · {form.comment.length}/3000)</span></label>
              <textarea data-testid="manual-review-comment" value={form.comment} onChange={set('comment')} rows={4} maxLength={3000} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" placeholder="Retranscrivez fidèlement l'avis reçu…" />
            </div>
          </div>

          <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer bg-gray-50 border rounded-lg p-3">
            <input type="checkbox" data-testid="manual-review-consent" checked={form.consent} onChange={(e) => setForm(f => ({ ...f, consent: e.target.checked }))} className="mt-0.5 rounded border-gray-300 text-orange-500" />
            <span>Je confirme que cet avis a été exprimé par un vrai client, qu'il est retranscrit fidèlement et que le client a accepté sa publication sous son prénom. Les avis fictifs sont interdits par Google et la DGCCRF.</span>
          </label>
        </div>
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} data-testid="manual-review-cancel">Annuler</Button>
          <Button onClick={submit} disabled={saving || !form.consent} className="bg-[#1e3a5f] hover:bg-[#2d4a6f]" data-testid="manual-review-submit">
            {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Check size={16} className="mr-2" />} Publier l'avis
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ManualReviewModal;
